import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../config";
import axiosInstance from '../../../utils/axiosInstance'
import "./UpdateEmp.css";

function UpdateEmp() {
  const { employeeId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    ename: "",
    dateOfBirth: "",
    gender: "",
    phoneNo: "",
    personal_email: "",
    official_email: "",
    fatherName: "",
    motherName: "",
    address: "",
    emergencyContact: "",
    relation: "",
    bankName: "",
    accountNo: "",
    ifscCode: "",
    bankAddress: "",
    accountHolderName: "",
    adarCardNo: "",
    panNo: "",
    qualification: "",
    lastExp: "",
    expWithPWT: "",
    department: "",
    service: "",
    interviewDate: "",
    joiningDate: "",
    expectedSalary: "",
    givenSalary: "",
    workingTime: "",
    userType: ""
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [imgFile, setImgFile] = useState(null);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [errors, setErrors] = useState({});

  // Regex Patterns
  const regex = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^[0-9]{10}$/,
    aadhaar: /^[0-9]{12}$/,
    pan: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    account: /^[0-9]{9,18}$/,
    ifsc: /^[A-Z]{4}0[0-9A-Z]{6}$/
  };

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getEmpDataByID/${employeeId}`)
      .then((res) => {
        const raw = res.data;
        const keys = [
          "ename", "dateOfBirth", "gender", "phoneNo", "personal_email", "official_email",
          "fatherName", "motherName", "address", "emergencyContact", "relation",
          "bankName", "accountNo", "ifscCode", "bankAddress", "accountHolderName",
          "adarCardNo", "panNo", "qualification", "lastExp", "expWithPWT",
          "department", "service", "interviewDate", "joiningDate", "expectedSalary",
          "givenSalary", "workingTime", "userType"
        ];
        
        const normalized = {};
        keys.forEach((key) => {
          if (raw[key] !== undefined && raw[key] !== null) {
            normalized[key] = raw[key];
          } else {
            normalized[key] = "";
          }
        });

        // Resolve populated references to their IDs
        if (raw.department && typeof raw.department === "object") {
          normalized.department = raw.department._id || "";
        }
        if (raw.service && typeof raw.service === "object") {
          normalized.service = raw.service._id || "";
        }

        // Also retain existing file paths since they need to be displayed/linked in the UI
        if (raw.resumeFile) normalized.resumeFile = raw.resumeFile;
        if (raw.img) normalized.img = raw.img;
        if (raw.aadhaarFile) normalized.aadhaarFile = raw.aadhaarFile;
        if (raw.panFile) normalized.panFile = raw.panFile;

        setFormData(normalized);
      })
      .catch((err) => console.log(err));
  }, [employeeId]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getDepartment`)
      .then((res) => setDepartments(res.data))
      .catch((err) => console.error("Error fetching departments:", err));
  }, []);

  useEffect(() => {
    if (!formData.department) return;
    axios
      .get(`${API_URL}/api/getServicebyDepartment/${formData.department}`)
      .then((res) => setServices(res.data))
      .catch((err) => console.error("Error fetching services:", err));
  }, [formData.department]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    let val = value;

    // Numeric-only fields
    const numberOnlyFields = [
      "phoneNo", "emergencyContact", "adarCardNo", "accountNo", "expectedSalary", "givenSalary", "workingTime"
    ];
    if (numberOnlyFields.includes(name)) {
      val = value.replace(/[^0-9]/g, "");
    }

    // Auto-uppercase fields
    if (name === "panNo" || name === "ifscCode") {
      val = value.toUpperCase();
    }

    // If department changes reset service
    if (name === "department") {
      setFormData(prev => ({ ...prev, department: val, service: '' }));
      setErrors(prev => ({ ...prev, department: '', service: '' }));
      return;
    }

    const updatedValue = type === 'file' ? files[0] : val;
    setFormData(prev => ({ ...prev, [name]: updatedValue }));

    // Clear error for this field immediately
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  const handleResumeChange = (e) => setResumeFile(e.target.files[0]);
  const handleImgChange = (e) => setImgFile(e.target.files[0]);
  
  const handleAadhaarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const fileExt = file.name.split('.').pop().toLowerCase();
      const isImage = allowedTypes.includes(file.type) || ['jpg', 'jpeg', 'png'].includes(fileExt);
      if (!isImage) {
        alert("❌ Only JPG or PNG formats are allowed for Aadhaar upload.");
        e.target.value = ""; // clear input
        return;
      }
      setAadhaarFile(file);
    }
  };

  const handlePanChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const fileExt = file.name.split('.').pop().toLowerCase();
      const isImage = allowedTypes.includes(file.type) || ['jpg', 'jpeg', 'png'].includes(fileExt);
      if (!isImage) {
        alert("❌ Only JPG or PNG formats are allowed for PAN upload.");
        e.target.value = ""; // clear input
        return;
      }
      setPanFile(file);
    }
  };

  // Validate all fields, return errors object
  const validateAll = () => {
    const newErrors = {};
    const today = new Date().toISOString().split("T")[0];

    // Required checks (you can tweak which are required)
    if (!formData.ename || !formData.ename.trim()) newErrors.ename = "Employee name is required.";
    if (!formData.phoneNo) newErrors.phoneNo = "Contact number is required.";
    else if (!regex.phone.test(formData.phoneNo)) newErrors.phoneNo = "Phone number must be 10 digits.";

    if (formData.personal_email && !regex.email.test(formData.personal_email)) newErrors.personal_email = "Invalid personal email.";
    if (!formData.official_email) newErrors.official_email = "Official email is required.";
    else if (!regex.email.test(formData.official_email)) newErrors.official_email = "Invalid official email.";

    if (formData.adarCardNo && !regex.aadhaar.test(formData.adarCardNo)) newErrors.adarCardNo = "Aadhaar must be 12 digits.";
    if (formData.panNo && !regex.pan.test(formData.panNo)) newErrors.panNo = "PAN format invalid (e.g., ABCDE1234F).";

    if (formData.accountNo && !regex.account.test(formData.accountNo)) newErrors.accountNo = "Account number must be 9–18 digits.";
    if (formData.ifscCode && !regex.ifsc.test(formData.ifscCode)) newErrors.ifscCode = "IFSC invalid (e.g., SBIN0001234).";

    if (formData.dateOfBirth) {
      const dobValid = !isNaN(new Date(formData.dateOfBirth).getTime());
      if (!dobValid) newErrors.dateOfBirth = "Please enter a valid Date of Birth.";
      else if (formData.dateOfBirth > today) newErrors.dateOfBirth = "Date of Birth cannot be in the future.";
    }

    if (formData.interviewDate) {
      const intValid = !isNaN(new Date(formData.interviewDate).getTime());
      if (!intValid) newErrors.interviewDate = "Please enter a valid Interview Date.";
    }
    if (formData.joiningDate) {
      const joinValid = !isNaN(new Date(formData.joiningDate).getTime());
      if (!joinValid) newErrors.joiningDate = "Please enter a valid Joining Date.";
      if (formData.interviewDate && formData.joiningDate < formData.interviewDate) newErrors.joiningDate = "Joining date cannot be before interview date.";
    }

    if (!formData.department) newErrors.department = "Please select a department.";
    if (!formData.service) newErrors.service = "Please select a service.";
    if (!formData.gender) newErrors.gender = "Please select a gender.";
    if (!formData.userType) newErrors.userType = "Please select a user type.";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validated = validateAll();
    setErrors(validated);
    if (Object.keys(validated).length > 0) {
      const firstKey = Object.keys(validated)[0];
      const el = document.getElementsByName(firstKey)[0];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null && key !== "resumeFile" && key !== "img" && key !== "aadhaarFile" && key !== "panFile") {
          data.append(key, formData[key]);
        }
      });

      if (resumeFile) data.append("resumeFile", resumeFile);
      if (imgFile) data.append("img", imgFile);
      if (aadhaarFile) data.append("aadhaarFile", aadhaarFile);
      if (panFile) data.append("panFile", panFile);

      await axiosInstance.put(
        `${API_URL}/api/updateSignUser/${employeeId}`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      alert("Employee updated successfully!");
      if (formData.userType === "employee") navigate("/admin/Employee");
      else navigate("/admin/trainee");
    } catch (error) {
      console.error(error);
      alert("Error updating employee: " + error.message);
    }
  };

  return (
    <div className="container my-5 update-emp-page">
      <button className="btn btn-outline-secondary mb-3" onClick={() => navigate(-1)}>
        Back
      </button>
      <div className="card shadow-lg p-4 rounded-4 update-emp-card">
        <h3 className="text-center mb-4 fw-bold update-emp-title">
          Update Employee Details
        </h3>

        <form onSubmit={handleSubmit} className="row g-4 update-emp-form">
          {/* Basic Information */}
          <h5 className="fw-semibold mt-3 update-emp-section">Basic Information</h5>
          <div className="col-md-6">
            <label className="form-label">Employee Name</label>
            <input
              type="text"
              name="ename"
              value={formData.ename}
              onChange={handleChange}
              className={`form-control ${errors.ename ? 'is-invalid' : ''}`}
              placeholder="Enter employee name"
            />
            {errors.ename && <div className="text-danger small">{errors.ename}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Date of Birth</label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth?.split("T")[0] || ""}
              onChange={handleChange}
              className={`form-control ${errors.dateOfBirth ? 'is-invalid' : ''}`}
            />
            {errors.dateOfBirth && <div className="text-danger small">{errors.dateOfBirth}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Gender</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className={`form-select ${errors.gender ? 'is-invalid' : ''}`}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            {errors.gender && <div className="text-danger small">{errors.gender}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Phone Number</label>
            <input
              type="text"
              name="phoneNo"
              value={formData.phoneNo}
              onChange={handleChange}
              className={`form-control ${errors.phoneNo ? 'is-invalid' : ''}`}
              placeholder="Enter phone number"
              maxLength={10}
            />
            {errors.phoneNo && <div className="text-danger small">{errors.phoneNo}</div>}
          </div>

          {/* Contact Info */}
          <h5 className="fw-semibold mt-4 update-emp-section">Contact Information</h5>
          <div className="col-md-6">
            <label className="form-label">Personal Email</label>
            <input
              type="email"
              name="personal_email"
              value={formData.personal_email}
              onChange={handleChange}
              className={`form-control ${errors.personal_email ? 'is-invalid' : ''}`}
              placeholder="example@gmail.com"
            />
            {errors.personal_email && <div className="text-danger small">{errors.personal_email}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Official Email</label>
            <input
              type="email"
              name="official_email"
              value={formData.official_email}
              onChange={handleChange}
              className={`form-control ${errors.official_email ? 'is-invalid' : ''}`}
              placeholder="official@company.com"
            />
            {errors.official_email && <div className="text-danger small">{errors.official_email}</div>}
          </div>

          {/* Family Info */}
          <h5 className="fw-semibold mt-4 update-emp-section">Family Information</h5>
          <div className="col-md-6">
            <label className="form-label">Father Name</label>
            <input
              type="text"
              name="fatherName"
              value={formData.fatherName}
              onChange={handleChange}
              className={`form-control ${errors.fatherName ? 'is-invalid' : ''}`}
            />
            {errors.fatherName && <div className="text-danger small">{errors.fatherName}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Mother Name</label>
            <input
              type="text"
              name="motherName"
              value={formData.motherName}
              onChange={handleChange}
              className={`form-control ${errors.motherName ? 'is-invalid' : ''}`}
            />
            {errors.motherName && <div className="text-danger small">{errors.motherName}</div>}
          </div>

          <div className="col-md-12">
            <label className="form-label">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              className={`form-control ${errors.address ? 'is-invalid' : ''}`}
              rows="2"
              placeholder="Enter full address"
            />
            {errors.address && <div className="text-danger small">{errors.address}</div>}
          </div>

          {/* Emergency Contact */}
          <h5 className="fw-semibold mt-4 update-emp-section">Emergency Contact</h5>
          <div className="col-md-6">
            <label className="form-label">Emergency Contact</label>
            <input
              type="text"
              name="emergencyContact"
              value={formData.emergencyContact}
              onChange={handleChange}
              className={`form-control ${errors.emergencyContact ? 'is-invalid' : ''}`}
            />
            {errors.emergencyContact && <div className="text-danger small">{errors.emergencyContact}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Relation</label>
            <input
              type="text"
              name="relation"
              value={formData.relation}
              onChange={handleChange}
              className={`form-control ${errors.relation ? 'is-invalid' : ''}`}
            />
            {errors.relation && <div className="text-danger small">{errors.relation}</div>}
          </div>

          {/* Bank Details */}
          <h5 className="fw-semibold mt-4 update-emp-section">Bank Details</h5>
          <div className="col-md-6">
            <label className="form-label">Bank Name</label>
            <input
              type="text"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
              className={`form-control ${errors.bankName ? 'is-invalid' : ''}`}
            />
            {errors.bankName && <div className="text-danger small">{errors.bankName}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Account Number</label>
            <input
              type="text"
              name="accountNo"
              value={formData.accountNo}
              onChange={handleChange}
              className={`form-control ${errors.accountNo ? 'is-invalid' : ''}`}
            />
            {errors.accountNo && <div className="text-danger small">{errors.accountNo}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">IFSC Code</label>
            <input
              type="text"
              name="ifscCode"
              value={formData.ifscCode}
              onChange={handleChange}
              className={`form-control ${errors.ifscCode ? 'is-invalid' : ''}`}
            />
            {errors.ifscCode && <div className="text-danger small">{errors.ifscCode}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Bank Address or Branch Name</label>
            <input
              type="text"
              name="bankAddress"
              value={formData.bankAddress || ""}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Account Holder Name</label>
            <input
              type="text"
              name="accountHolderName"
              value={formData.accountHolderName}
              onChange={handleChange}
              className={`form-control ${errors.accountHolderName ? 'is-invalid' : ''}`}
            />
            {errors.accountHolderName && <div className="text-danger small">{errors.accountHolderName}</div>}
          </div>

          {/* ID Proofs */}
          <h5 className="fw-semibold mt-4 update-emp-section">ID Proofs</h5>
          <div className="col-md-6">
            <label className="form-label">Aadhaar Card Number</label>
            <input
              type="text"
              name="adarCardNo"
              value={formData.adarCardNo}
              onChange={handleChange}
              className={`form-control ${errors.adarCardNo ? 'is-invalid' : ''}`}
            />
            {errors.adarCardNo && <div className="text-danger small">{errors.adarCardNo}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Upload Aadhaar Card (JPG/PNG)</label>
            <input
              type="file"
              name="aadhaarFile"
              onChange={handleAadhaarChange}
              className="form-control"
              accept=".jpg,.jpeg,.png"
            />
            {formData.aadhaarFile && (
              <div className="mt-1 small">
                <a href={formData.aadhaarFile} target="_blank" rel="noopener noreferrer" className="text-primary">
                  View Current Aadhaar Card
                </a>
              </div>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label">PAN Number</label>
            <input
              type="text"
              name="panNo"
              value={formData.panNo}
              onChange={handleChange}
              className={`form-control ${errors.panNo ? 'is-invalid' : ''}`}
            />
            {errors.panNo && <div className="text-danger small">{errors.panNo}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Upload PAN Card (JPG/PNG)</label>
            <input
              type="file"
              name="panFile"
              onChange={handlePanChange}
              className="form-control"
              accept=".jpg,.jpeg,.png"
            />
            {formData.panFile && (
              <div className="mt-1 small">
                <a href={formData.panFile} target="_blank" rel="noopener noreferrer" className="text-primary">
                  View Current PAN Card
                </a>
              </div>
            )}
          </div>

          {/* Professional Info */}
          <h5 className="fw-semibold mt-4 update-emp-section">Professional Details</h5>
          <div className="col-md-6">
            <label className="form-label">Qualification</label>
            <input
              type="text"
              name="qualification"
              value={formData.qualification}
              onChange={handleChange}
              className={`form-control ${errors.qualification ? 'is-invalid' : ''}`}
            />
            {errors.qualification && <div className="text-danger small">{errors.qualification}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Last Experience</label>
            <input
              type="text"
              name="lastExp"
              value={formData.lastExp}
              onChange={handleChange}
              className={`form-control ${errors.lastExp ? 'is-invalid' : ''}`}
            />
            {errors.lastExp && <div className="text-danger small">{errors.lastExp}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Experience with PWT</label>
            <input
              type="text"
              name="expWithPWT"
              value={formData.expWithPWT}
              onChange={handleChange}
              className={`form-control ${errors.expWithPWT ? 'is-invalid' : ''}`}
            />
            {errors.expWithPWT && <div className="text-danger small">{errors.expWithPWT}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Select Department</label>
            <select
              className={`form-select ${errors.department ? 'is-invalid' : ''}`}
              name="department"
              value={formData.department}
              onChange={handleChange}
            >
              <option value="">-- Select Department --</option>
              {departments.map((dept) => (
                <option key={dept._id} value={dept._id}>
                  {dept.deptName}
                </option>
              ))}
            </select>
            {errors.department && <div className="text-danger small">{errors.department}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Select Service</label>
            <select
              className={`form-select ${errors.service ? 'is-invalid' : ''}`}
              name="service"
              value={formData.service}
              onChange={handleChange}
            >
              <option value="">-- Select Service --</option>
              {services.map((srv) => (
                <option key={srv._id} value={srv._id}>
                  {srv.serviceName}
                </option>
              ))}
            </select>
            {errors.service && <div className="text-danger small">{errors.service}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Interview Date</label>
            <input
              type="date"
              name="interviewDate"
              value={formData.interviewDate?.split("T")[0] || ""}
              onChange={handleChange}
              className={`form-control ${errors.interviewDate ? 'is-invalid' : ''}`}
            />
            {errors.interviewDate && <div className="text-danger small">{errors.interviewDate}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Joining Date</label>
            <input
              type="date"
              name="joiningDate"
              value={formData.joiningDate?.split("T")[0] || ""}
              onChange={handleChange}
              className={`form-control ${errors.joiningDate ? 'is-invalid' : ''}`}
            />
            {errors.joiningDate && <div className="text-danger small">{errors.joiningDate}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Expected Salary</label>
            <input
              type="text"
              name="expectedSalary"
              value={formData.expectedSalary}
              onChange={handleChange}
              className={`form-control ${errors.expectedSalary ? 'is-invalid' : ''}`}
            />
            {errors.expectedSalary && <div className="text-danger small">{errors.expectedSalary}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Given Salary</label>
            <input
              type="text"
              name="givenSalary"
              value={formData.givenSalary}
              onChange={handleChange}
              className={`form-control ${errors.givenSalary ? 'is-invalid' : ''}`}
            />
            {errors.givenSalary && <div className="text-danger small">{errors.givenSalary}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Working Time</label>
            <input
              type="text"
              name="workingTime"
              value={formData.workingTime}
              onChange={handleChange}
              className={`form-control ${errors.workingTime ? 'is-invalid' : ''}`}
            />
            {errors.workingTime && <div className="text-danger small">{errors.workingTime}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">User Type</label>
            <select
              name="userType"
              value={formData.userType}
              onChange={handleChange}
              className={`form-select ${errors.userType ? 'is-invalid' : ''}`}
            >
              <option value="">Select Type</option>
              <option value="employee">Employee</option>
              <option value="trainee">Trainee</option>
            </select>
            {errors.userType && <div className="text-danger small">{errors.userType}</div>}
          </div>

          {/* Upload Section */}
          <h5 className="fw-semibold mt-4 update-emp-section">Documents</h5>
          <div className="col-md-6">
            <label className="form-label">Upload Resume</label>
            <input
              type="file"
              name="resumeFile"
              onChange={handleResumeChange}
              className={`form-control ${errors.resumeFile ? 'is-invalid' : ''}`}
              accept=".pdf,.doc,.docx"
            />
            {errors.resumeFile && <div className="text-danger small">{errors.resumeFile}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Upload Image</label>
            <input
              type="file"
              name="img"
              onChange={handleImgChange}
              className={`form-control ${errors.img ? 'is-invalid' : ''}`}
              accept="image/*"
            />
            {errors.img && <div className="text-danger small">{errors.img}</div>}
          </div>

          <div className="col-md-12 text-center mt-4">
            <button type="submit" className="btn update-emp-submit px-5 py-2 shadow-sm">
              Update User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateEmp;
